import {
  Box,
  Flex,
  HStack,
  Link,
  LinkBox,
  LinkOverlay,
  Stack,
  VStack,
} from '@cardboard-ui/react';
import { t, Trans } from '@lingui/macro';
import { lazy } from '@loadable/component';
import { ItemLocationInfo } from 'apps/TenantApp/components/ItemLocationInfo';
import { Card } from 'components/cards/Card';
import {
  BullhornIcon,
  CalendarAltIcon,
  FileAltIcon,
  FolderOpenIcon,
  FormIcon,
  UsersIcon,
  VaultIcon,
  WarningIcon,
} from 'components/icons';
import { FileIcon } from 'components/icons/FileIcon';
import SpaceIcon from 'components/SpaceIcon';
import { UserAvatar } from 'components/UserAvatar';
import React, { FC, PropsWithChildren } from 'react';
import { generatePath, To } from 'react-router-dom';
import { graphql } from 'relay-runtime';
import { RemirrorJSON } from 'remirror';
import { useFragment } from 'utils/graphClient';
import { useI18n } from 'utils/i18n';
import {
  ANNOUNCEMENT_VIEWER_QUERY_PARAM_NAME,
  CANONICAL_ITEM_PATH,
  CANONICAL_PERSON_PATH,
  ITEM_VIEWER_QUERY_PARAM_NAME,
  MEMBER_PATH,
  SEARCH_MODAL_QUERY_PARAM_NAME,
  SPACE_PATH,
  VAULTS_MANAGER_VAULT_PATH,
} from 'utils/routes';
import { SearchResult$key } from './__generated__/SearchResult.graphql';
import { TextLink } from 'apps/TenantApp/components/TextLink';

const RemirrorRenderer = lazy(() => import('components/Editor/Renderer'));

const searchResultFragment = graphql`
  fragment SearchResult on SearchResult {
    node {
      id
      __typename
      ... on Member {
        name
        ...UserAvatar_member
      }
      ... on Space {
        name
        color {
          light
          dark
        }
      }
      ... on Event {
        name
        ...ItemLocationInfo_item
      }
      ... on Folder {
        name
        ...ItemLocationInfo_item
      }
      ... on FolderItem {
        name
        content {
          __typename
          ... on File {
            mimeType
          }
          ... on Page {
            id
          }
          ... on Form {
            id
          }
        }
        ...ItemLocationInfo_item
      }
      ... on VaultItem {
        name
        content {
          __typename
          ... on File {
            mimeType
          }
        }
        vault {
          id
          name
        }
      }
      ... on Announcement {
        announcer {
          name
        }
        space {
          id
        }
        publishedAt
        message
        ...ItemLocationInfo_item
      }
      ... on Genealogy__Person {
        name
      }
    }
  }
`;

export const SearchResult = ({ result }: { result: SearchResult$key }) => {
  const searchResultNode = useFragment(searchResultFragment, result);
  const item = searchResultNode.node;
  const { format } = useI18n();

  switch (item.__typename) {
    case 'Event':
      return (
        <SearchCard
          name={item.name}
          icon={<CalendarAltIcon />}
          to={{
            pathname: generatePath(CANONICAL_ITEM_PATH, { itemId: item.id }),
          }}
        >
          <ItemLocationInfo item={item} />
        </SearchCard>
      );
    case 'Folder':
      return (
        <SearchCard
          name={item.name}
          icon={<FolderOpenIcon />}
          to={{
            pathname: generatePath(CANONICAL_ITEM_PATH, { itemId: item.id }),
          }}
        >
          <ItemLocationInfo item={item} />
        </SearchCard>
      );
    case 'Member':
      return (
        <SearchCard
          name={item.name}
          icon={<UserAvatar member={item} size="sm" />}
          to={{
            pathname: generatePath(MEMBER_PATH, { memberId: item.id }),
          }}
        />
      );
    case 'Genealogy__Person':
      return (
        <SearchCard
          name={item.name}
          icon={<UsersIcon size="sm" />}
          to={{
            pathname: generatePath(CANONICAL_PERSON_PATH, {
              personId: item.id,
            }),
          }}
        />
      );
    case 'Space':
      return (
        <SearchCard
          name={item.name}
          icon={<SpaceIcon color={item.color?.light} />}
          to={{
            pathname: generatePath(SPACE_PATH, { spaceId: item.id }),
          }}
        />
      );
    case 'FolderItem':
      return (
        <SearchCard
          name={item.name}
          icon={
            <FolderItemIcon
              contentTypeName={item.content?.__typename || '%other'}
              mimeType={
                item.content?.__typename === 'File'
                  ? item.content.mimeType
                  : undefined
              }
            />
          }
          to={{
            pathname: undefined,
            params: new URLSearchParams(
              `?${ITEM_VIEWER_QUERY_PARAM_NAME}=${item.id}`,
            ),
          }}
        >
          <ItemLocationInfo item={item} />
        </SearchCard>
      );
    case 'VaultItem':
      return (
        <SearchCard
          name={item.name}
          icon={
            <FolderItemIcon
              contentTypeName={item.content?.__typename || '%other'}
              mimeType={
                item.content?.__typename === 'File'
                  ? item.content.mimeType
                  : undefined
              }
            />
          }
          to={{
            pathname: undefined,
            params: new URLSearchParams(
              `?${ITEM_VIEWER_QUERY_PARAM_NAME}=${item.id}`,
            ),
          }}
        >
          {!!item.vault && (
            <HStack fontSize="xs" spacing={0.5} display="inline-flex">
              <VaultIcon />
              <TextLink
                to={generatePath(VAULTS_MANAGER_VAULT_PATH, {
                  vaultId: item.vault.id,
                })}
              >
                {item.vault.name}
              </TextLink>
            </HStack>
          )}
        </SearchCard>
      );
    case 'Announcement': {
      if (!item.announcer || !item.publishedAt) {
        return (
          <SearchCard
            icon={<WarningIcon />}
            name={t`Unable to render announcement.`}
          />
        );
      }
      const announcer = <b>{item.announcer.name}</b>;
      const announcementPublishDate = (
        <b>{format(new Date(item.publishedAt), 'PPP')}</b>
      );

      return (
        <SearchCard
          name={
            <Trans>
              {announcer} on {announcementPublishDate}
            </Trans>
          }
          icon={<BullhornIcon />}
          to={{
            pathname: undefined,
            params: new URLSearchParams(
              `?${ANNOUNCEMENT_VIEWER_QUERY_PARAM_NAME}=${item.id}`,
            ),
          }}
        >
          <Stack w="100%" spacing={3}>
            {item.message ? (
              <Flex
                flex={1}
                borderRadius="md"
                bg="gray.50"
                _dark={{ bg: 'gray.600' }}
                py={2}
                px={4}
                m={1}
              >
                <RemirrorRenderer json={item.message as RemirrorJSON} />
              </Flex>
            ) : null}
            <ItemLocationInfo item={item} />
          </Stack>
        </SearchCard>
      );
    }
    default:
      return (
        <SearchCard icon={<WarningIcon />} name={t`Unable to render result.`} />
      );
  }
};

type FolderItemIconProps = {
  contentTypeName: 'Page' | 'Form' | '%other' | 'File';
  mimeType?: string;
};

const FolderItemIcon = ({ contentTypeName, mimeType }: FolderItemIconProps) => {
  switch (contentTypeName) {
    case 'Page':
      return <FileAltIcon />;
    case 'Form':
      return <FormIcon />;
    case 'File':
    default:
      return <FileIcon mimeType={mimeType} />;
  }
};

interface SearchCardProps {
  icon: React.ReactNode;
  name: React.ReactNode;
  to?: SearchResultTo;
}

const SearchCard: FC<PropsWithChildren<SearchCardProps>> = ({
  icon,
  children,
  name,
  to,
}) => {
  return (
    <LinkBox>
      <Card isNotClickable={!to} p={2}>
        <VStack align="start" spacing={0} flex={1}>
          <HStack w="100%">
            <Box fontSize="xl" color="blue.500">
              {icon}
            </Box>
            <LinkOverlay
              as={Link}
              to={to ? closeSearchModalTo(to) : undefined}
              w="100%"
            >
              {name}
            </LinkOverlay>
          </HStack>
          <Box pl={8} w="100%">
            {children}
          </Box>
        </VStack>
      </Card>
    </LinkBox>
  );
};

interface SearchResultTo {
  pathname: string | undefined;
  params?: URLSearchParams;
}

function closeSearchModalTo({ pathname, params }: SearchResultTo): To {
  const newParams = params || new URLSearchParams(window.location.search);
  newParams.delete(SEARCH_MODAL_QUERY_PARAM_NAME);
  return {
    pathname,
    search: newParams.toString(),
  };
}
